;; Copyright (C) 2015, Doctor Evidence. All rights reserved.

(ns dre.util
  "Assorted utilities."
  {:authors ["Jozef Wagner" "Joel Kuiper" "Michiel Borkent"]}
  (:refer-clojure #?@(:cljs [:exclude [keyword-identical?]]))
  (:require
   #?(:clj [net.cgrand.macrovich :as macros])
   #?(:clj [clojure.pprint :refer [pprint]])
   #?(:clj [schema.core])
   [clojure.walk :as walk]
   [clojure.string :as str]
   [com.stuartsierra.component]
   #?(:clj [suspendable.core])
   #?(:clj [clojure.tools.logging :refer [warn]])
   #?(:cljs [goog.string :as gstr])
   #?(:cljs [goog.string.format]))
  #?(:clj (:import [java.util.concurrent ConcurrentHashMap]))
  #?(:cljs
     (:import [goog.string StringBuffer]))
  #?(:cljs
     (:require-macros
      [net.cgrand.macrovich :as macros]
      ;; cljs must self refer macros
      [dre.util :refer [if-let* when-let* cond* defcomponent
                        log]])))


;;;; Math

(defn xnor
  "Returns result of logical XNOR of x and y."
  [x y]
  (or (and x y) (and (not x) (not y))))

(defn zdiv
  "Like cc//, but returns 0 if denominator is 0."
  [num den]
  (if (zero? den)
    0
    (/ num den)))


;;;; Control Flow

#?(:clj
   (defmacro if-let*
     "binding => binding-form test

     If all tests are logical `true`, evaluates `_then_`
     with binding-forms bound to the value of tests, if not, yields
     `_else_`. Accepts multiple bindings.

     Supports `:let` modifier as binding-form, in which case it treats
     a corresponding test as a bindings vec. This is used to introduce
     locals that are not subject to the trueness comparison.

     `_else_` clause must not use any binding-form or any custom
     locals created with `:let` modifier in any way.

     WARNING: A subset of the bound locals may be visible in the
     `_else_` expr. Do not rely on this behaviour."
     ([bindings then]
      `(if-let* ~bindings ~then nil))
     ([bindings then else & oldform]
      (if (seq bindings)
        (let [form (first bindings)
              tst (second bindings)
              rst (rest (rest bindings))]
          (if (identical? :let form)
            `(let ~tst (if-let* ~rst ~then ~else nil))
            `(let [temp# ~tst]
               (if temp#
                 (let [~form temp#] (if-let* ~rst ~then ~else nil))
                 ~else))))
        then))))

#?(:clj
   (defmacro when-let*
     "binding => binding-form test

     When test is logically `true`, evaluates `_body_` with
     binding-form bound to the value of test. Accepts multiple
     bindings. Evaluates `_body_` only if all test are logically
     `true`.

     Supports `:let` modifier as binding-form, in which case it treats
     a corresponding test as a bindings vec. This is used to introduce
     locals that are not subject to the trueness comparison."
     [bindings & body]
     `(if-let* ~bindings (do ~@body))))

#?(:clj
   (defmacro cond*
     "Takes a set of `test`/`expr` pairs. It evaluates each `test` one
     at a time. If a `test` returns logical `true`, `cond` evaluates
     and returns the value of the corresponding `expr` and doesn’t
     evaluate any of the other tests or exprs. `(cond)` returns `nil`.

     Treats last argument as an implicit else, if number of arguments
     is odd. If `test` is a vector, treats `test` as input to
     `if-let`.

     `cond` supports several modifiers:

     * `:let` - treats `expr` as a binding vec and establishes custom
       locals that will be available for subsequent `test`/`expr`
       pairs
     * `:when` - immediatelly returns `nil` if `expr` does not
       evaluate to logical `true`. Continues with the rest of pairs
       otherwise.
     * `:when-let` - similar to `:when`, but treats `expr` as an input
       to `when-let`.

     Note that modifiers cannot be at a tail position. Moreover, it is
     not idiomatic to use modifier as first `test`."
     [& clauses]
     (when-let [[tst exp & rst] (seq clauses)]
       (cond
         (not (next clauses)) tst
         (and (not (seq rst))
              (or (identical? :let tst)
                  (identical? :when tst)
                  (identical? :when-let tst)))
         (throw (java.lang.IllegalArgumentException.
                 "cond* modifiers at tail position"))
         (identical? :let tst) `(let ~exp (cond* ~@rst))
         (identical? :when tst) `(when ~exp (cond* ~@rst))
         (identical? :when-let tst) `(when-let* ~exp (cond* ~@rst))
         (vector? tst) `(if-let* ~tst ~exp (cond* ~@rst))
         :else `(if ~tst ~exp (cond* ~@rst))))))

#?(:clj
   ;; from https://github.com/flatland/useful/blob/develop/src/flatland/useful/experimental.clj#L31
   (defmacro cond-let
     "An implementation of cond-let that is as similar as possible to if-let. Takes multiple
  test-binding/then-form pairs and evalutes the form if the binding is true. Also supports
  :else in the place of test-binding and always evaluates the form in that case.
  Example:
   (cond-let [b (bar 1 2 3)] (println :bar b)
             [f (foo 3 4 5)] (println :foo f)
             [b (baz 6 7 8)] (println :baz b)
             :else           (println :no-luck))"
     [test-binding then-form & more]
     (let [test-binding (if (= :else test-binding) `[t# true] test-binding)
           else-form    (when (seq more) `(cond-let ~@more))]
       `(if-let ~test-binding
          ~then-form
          ~else-form))))

#?(:clj
   (defmacro not-implemented
     "Returns code that throws unsupported operation exception when
     evaled."
     []
     `(java.lang.UnsupportedOperationException.)))

;;;; Host, memory

(def ^java.util.concurrent.ConcurrentHashMap intern-map
  "A var for holding interned objects."
  #?(:cljs (atom {})
     :clj (java.util.concurrent.ConcurrentHashMap.)))

(defn interned-object
  "Returns an interned version of object x.
  Uses = for equality, keeps object in memory indefinitely."
  [x]
  #?(:cljs (let [uf #(if (contains? % %2) % (assoc % %2 %2))]
             (swap! intern-map uf x)
             (get @intern-map x))
     :clj (or (.putIfAbsent intern-map x x) x)))

(defn interned
  "Returns an interned version of object x.
  Uses = for equality, keeps object in memory indefinitely.
  Uses host's optimizations."
  [x]
  #?(:cljs (interned-object x)
     :clj (interned-object x)
     ;; see https://shipilev.net/jvm-anatomy-park/10-string-intern/
     #_(if (string? x)
         (.intern ^String x)
         (interned-object x))))

(defn interned-keys
  "Returns a map m with values for k's switched to interned ones."
  ([m k]
   (if-let [v (get m k)]
     (assoc m k (interned v))
     m))
  ([m k1 k2]
   (-> m (interned-keys k1) (interned-keys k2)))
  ([m k1 k2 k3]
   (-> m (interned-keys k1) (interned-keys k2) (interned-keys k3)))
  ([m k1 k2 k3 k4]
   (-> m (interned-keys k1) (interned-keys k2)
       (interned-keys k3) (interned-keys k4))))

#?(:clj
   (defn call-constructor
     "Calls a private or public sonstructor of klass that has
     given params (a collection of classes), passing args as argument
     list into constructor."
     [klass params & args]
     (-> klass (.getDeclaredConstructor (into-array Class params))
         (doto (.setAccessible true))
         (.newInstance (into-array Object args)))))

#?(:clj
   (defn get-field
     "Returns obj's private or public field with given field-name,
     defined in klass. Pass nil into obj for static fields."
     [klass field-name obj]
     (-> klass (.getDeclaredField (name field-name))
         (doto (.setAccessible true))
         (.get obj))))


;;;; URI

#?(:clj
   (defn ^java.net.URI provide-uri
     "Returns uri from given arguments, with optional prefix."
     ([x]
      (cond (nil? x) nil
            (string? x) (java.net.URI. x)
            :else x))
     ([prefix x]
      (let [^java.net.URI uri (provide-uri x)
            ^java.net.URI prefix-uri (provide-uri prefix)]
        (if prefix-uri (.resolve prefix-uri uri) uri)))))

(declare empty-string?)

#?(:clj
   (defn url-path
     "Returns a url path string that is the combination of given
     parent and children, adding/removing slashes on the parent-child
     boundaries as needed."
     (^String []
      "")
     (^String [^String parent]
      (or parent ""))
     (^String [^String parent ^String child]
      (let [ep? (empty-string? parent)
            ec? (empty-string? child)
            ps? (and (not ep?)
                     (= \/ (.charAt parent (dec (count parent)))))
            cs? (and (not ec?)
                     (= \/ (.charAt child 0)))]
        (cond ec? parent
              (and ps? cs?) (str parent (subs child 1))
              (and (not ps?) (not cs?)) (str parent "/" child)
              :else (str parent child))))
     (^String [^String parent ^String child & children]
      (apply url-path (url-path parent child) children)))
   :cljs
   (defn url-path
     "Returns a url path string that is the combination of given
     parent and children, adding/removing slashes on the parent-child
     boundaries as needed."
     ([]
      "")
     ([parent]
      (or parent ""))
     ([parent child]
      (let [ep? (empty-string? parent)
            ec? (empty-string? child)
            ps? (and (not ep?)
                     (= \/ (.charAt parent (dec (count parent)))))
            cs? (and (not ec?)
                     (= \/ (.charAt child 0)))]
        (cond ec? parent
              (and ps? cs?) (str parent (subs child 1))
              (and (not ps?) (not cs?)) (str parent "/" child)
              :else (str parent child))))
     ([parent child & children]
      (apply url-path (url-path parent child) children))))

(defn ^String url-encode
  "Returns URL encoded string s. Useful when sending string as a
  query param. If used to encode form data (URL query after the ? char
  or in POST body), set x-www-form-urlencoded? to true to use +
  instead of %20 for spaces.

  WARNING: Not useful for HTML escaping, as it does not escape ' char"
  ([^String s]
   (url-encode s nil))
  ([^String s x-www-form-urlencoded?]
   #?(:clj
      (when-let [s (some-> s (java.net.URLEncoder/encode "UTF-8"))]
        (if x-www-form-urlencoded? s (.replace s "+" "%20")))
      :cljs
      (when-let [s (some-> s
                           js/encodeURIComponent
                           (.replace (js/RegExp. "[!'()*]" "g")
                                     #(str "%" (-> %
                                                   (.charCodeAt 0)
                                                   (.toString 16)))))]
        (if x-www-form-urlencoded? (.replace s "%20" "+") s)))))


;;;; Common business logic

(def acronym-override-map
  {"Stage III" false
   "Stage-III" false})

(defn acronym-override
  [text]
  (get acronym-override-map text nil))

(defn acronym?
  "Returns true if given [from to] interval of text is acronym,
  otherwise returns false."
  ([text]
   (let [override (acronym-override text)
         s (seq text)
         uf #?(:clj #(Character/isUpperCase ^char %)
               :cljs #(= (str/upper-case %) %))
         lf #?(:clj #(Character/isLetter ^char %)
               :cljs #(gstr/isAlpha %))
         uppers (count (filter uf s))
         letters (count (filter lf s))]
     (if (nil? override)
       (and (< 0 letters 15)
            (<= (/ 1 2) (/ uppers letters)))
       override)))
  ([text from to]
   (acronym? (subs text from to))))

;;;; Component helpers

#?(:clj
   (defn gennew
     "Returns forms that make up a component constructor function."
     [body cname rname docstring]
     (let [fname (symbol (str "new-" (str/lower-case (name cname))))
           body (cond (empty? body) '(([] {}))
                      (vector? (first body)) [body]
                      :else body)
           constructor (symbol (str "map->" (name rname)))
           mf (fn [[args & body]]
                (list args
                      `(let [m# (do ~@body)] (~constructor m#))))]
       `(defn ~fname ~docstring
          ~@(map mf body)))))

#?(:clj
   (defn genstart
     "Returns forms that make up a component start method."
     [body core explicit?]
     (let [component (or (ffirst body) (gensym "component"))
           mf (fn [sym] `(~(keyword sym) ~component))
           m `(do ~@(rest body))
           m (if explicit? m `{~(keyword core) ~m})]
       `(com.stuartsierra.component/start
         [~component]
         (if ~(mf core)
           ~component
           (let [m# ~m]
             (merge ~component m#)))))))

#?(:clj
   (defn genstop
     "Returns forms that make up a component stop method."
     [body core guarded]
     (let [component (or (ffirst body) (gensym "component"))
           nilmap (zipmap (map keyword guarded) (repeat nil))
           mf (fn [sym] `(~(keyword sym) ~component))]
       `(com.stuartsierra.component/stop
         [~component]
         (if ~(mf core)
           (let [~@(interleave guarded (map mf guarded))]
             (do ~@(rest body)
                 (merge ~component ~nilmap)))
           ~component)))))

#?(:clj (defn gensuspend
          "Returns forms that make up a component suspend method."
          [body]
          (let [component (or (ffirst body) (gensym "component"))]
            `(suspendable.core/suspend
              [~component]
              ~@(rest body)))))

#?(:clj (defn genresume
          "Returns forms that make up a component suspend method."
          [body]
          (let [component (or (ffirst body) (gensym "component"))
                old-component (or (second (first body))
                                  (gensym "old-component"))]
            `(suspendable.core/resume
              [~component ~old-component]
              ~@(rest body)))))

#?(:clj (defn gensuspendable
          [mm]
          (let [suspend-body (get mm 'suspend)
                resume-body (get mm 'resume)
                body-count (count
                            (keep identity
                                  [suspend-body resume-body]))
                suspendable? (= 2 body-count)]
            (assert (or (zero? body-count)
                        suspendable?)
                    "When implementing Suspendable provide both suspend and
            resume methods.")
            (when suspendable?
              `(suspendable.core/Suspendable
                ~(gensuspend suspend-body)
                ~(genresume resume-body))))))

#?(:clj
   (defmacro defcomponent
     "Creates Component record called <cname>Component and a
  respective constructor new-<lowercased-cname>.  First argument in
  args vector is treated as a core component property, that is checked
  for nil upon start and stop to determine whether component is
  started/stopped.  If args vector contains | symbol, arguments before
  it are treated as 'guarded', and are together with core property
  automatically localy binded and set to nil after stop method
  finishes.'  This macro accepts three required methods, start, stop
  and new and two optional methods suspend and resume.  Start and stop
  should have one argument that will be bind to the component. Start
  method should return a value for core property or a map if there are
  some guarded properties.  Return value of stop method is ignored and
  the method is run just for side effects.  Start method should return
  a map that will be merged to the newly created component. Suspend
  and resume are an optional implementation of the
  suspendable.core/Suspendable protocol.
  The & symbol is deprecated and replaced by the | symbol."
     {:style/indent [1 :form :form :form [1]]}
     [cname docstring args & methods]
     (assert (symbol? cname) "First argument must be a symbol")
     (assert (string? docstring) "Docstring is required")
     (assert (vector? args) "Argument vector is missing")
     (assert (pos? (count args))
             "There must be at least one argument")
     (let [mm (zipmap (map first methods) (map rest methods))
           explicit? (some #(or (= '& %) (= '| %)) args)
           core (first args)
           guarded (if explicit?
                     (take-while #(and (not (= '& %)) (not (= '| %))) args)
                     [core])
           rname (with-meta (symbol (str (name cname) "Component"))
                   (meta cname))
           component (gensym "component")
           start `~(genstart (get mm 'start) core explicit?)
           stop `~(genstop (get mm 'stop) core guarded)
           suspendable-body `~(gensuspendable mm)]
       `(let []
          (defrecord ~rname ~(vec (remove #(or (= '& %) (= '| %)) args))
            com.stuartsierra.component/Lifecycle
            ~start
            ~stop
            ~@suspendable-body)
          ~(gennew (get mm 'new) cname rname docstring)))))


;;;; Memoization

;;; fast-memoize
;;; Lifted from https://github.com/ztellman/potemkin/blob/master/src/potemkin/utils.clj
#?(:clj
   (definline re-nil [x]
     `(let [x# ~x]
        ;; TODO: use metric here
        ;;(warn "match-document cache hit")
        (if (identical? ::nil x#) nil x#))))

#?(:clj
   (definline de-nil [x]
     `(let [x# ~x]
        (if (nil? x#) ::nil x#))))

#?(:clj
   (defmacro memoize-form [m f kf & args]
     `(let [k# (~kf ~@args)]
        (let [v# (.get ~m k#)]
          (if-not (nil? v#)
            (re-nil v#)
            (let [v# (de-nil (~f ~@args))]
              (or (.putIfAbsent ~m k# v#) v#)))))))

#?(:clj
   (defn fast-memoize
     "A version of `memoize` which has equivalent behavior, but is
     faster. Takes a mandatory function kf that will be used to
     compute the cache/memoize key"
     ([f kf]
      (let [m (ConcurrentHashMap.)]
        (fn
          ([]
           (memoize-form m f kf))
          ([x]
           (memoize-form m f kf x))
          ([x y]
           (memoize-form m f kf x y))
          ([x y z]
           (memoize-form m f kf x y z))
          ([x y z w]
           (memoize-form m f kf x y z w))
          ([x y z w u]
           (memoize-form m f kf x y z w u))
          ([x y z w u v]
           (memoize-form m f kf x y z w u v))
          ([x y z w u v & rest]
           (let [k (apply kf (list* x y z w u v rest))]
             (let [v (.get ^ConcurrentHashMap m k)]
               (if-not (nil? v)
                 (re-nil v)
                 (let [v (de-nil (apply f k))]
                   (or (.putIfAbsent m k v) v)))))))))))


;;;; Debugging, profiling

#?(:clj
   (defmacro with-duration
     "Returns [duration result] pair of evaling body and measuring
     eval's duration."
     [& body]
     `(let [before# (System/currentTimeMillis)
            result# (do ~@body)]
        [(- (System/currentTimeMillis) before#) result#])))

(macros/deftime
  (defmacro log
    "Prints x into the console."
    [x]
    `(macros/case
         :cljs (.log js/console (cljs.core/clj->js ~x)))))


;;;; Network

;; TODO: Commented out because requiring [ajax.core :refer [POST]] throws error in storybook
;; TODO: Also it looks like this fn is not used anywhere else. Consider deleting it.
;; #?(:cljs
;;    (defn json-post!
;;      "Performs a POST request to the endpoint identified by given uri,
;;      using handler fn to procedd the response. Returns nil.
;;      A request param is sent as a POST body encoded in JSON."
;;      ([uri request handler]
;;       (json-post! uri request handler nil))
;;      ([uri request handler finally]
;;       (json-post! uri request handler nil finally))
;;      ([uri request handler error-handler finally]
;;       (POST uri {:format :json
;;                  :keywords? true
;;                  :response-format :json
;;                  :params request
;;                  :finally finally
;;                  :handler handler
;;                  :error-handler error-handler})
;;       nil)))


;;;; Assorted

(defn rand-uuid
  "Returns a type 4 random UUID: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx"
  []
  #?(:clj (java.util.UUID/randomUUID)
     :cljs (let [hexa #(.toString % 16)
                 r (repeatedly 30 #(hexa (rand-int 16)))
                 vals (concat (take 8 r) ["-"]
                              (take 4 (drop 8 r)) ["-4"]
                              (take 3 (drop 12 r)) ["-"]
                              [(->> (rand-int 15)
                                    (bit-and 0x3)
                                    (bit-or 0x8)
                                    hexa)]
                              (take 3 (drop 15 r)) ["-"]
                              (take 12 (drop 18 r)))]
             (apply str vals))))

(defn counts->percentages
  "Returns a collection of percentages given the
  collection of counts."
  ([counts total]
   (map #(float (* (/ %1 total) 100)) counts))
  ([counts]
   (counts->percentages counts (reduce + counts))))

(defn distinct-case-insensitive
  "Returns a sequence of strings, made distinct
  using case-insensitive comparison."
  [xs]
  (->> xs
       (group-by clojure.string/lower-case)
       (vals)
       (map first)))

#?(:cljs
   (defn format
     "Formats a string using goog.string.format."
     [fmt & args]
     (apply gstr/format fmt args)))

#?(:cljs
   (defn pprint-json
     "Pretty prints args into string."
     [& args]
     (.stringify js/JSON (clj->js args) nil 2)))

#?(:clj
   (defn ^Boolean empty-string?
     "Returns true if string is nil or empty."
     [^String string]
     (or (nil? string) (.isEmpty string))))

#?(:cljs
   (defn empty-string?
     "Returns true if string is nil or empty."
     [string]
     (or (nil? string) (gstr/isEmpty string))))

#?(:clj
   (defn ^Boolean solid-string?
     "Returns true if string is not blank."
     [^String string]
     (not (str/blank? string))))

#?(:cljs
   (defn solid-string?
     "Returns true if string is nil or empty."
     [string]
     (not (str/blank? string))))

(defn rcompare
  [x y]
  (compare y x))

(defn into-string
  "Returns a string that contains items from from joined into to."
  ([] "")
  ([to] to)
  ([to from]
   (let [sb #?(:cljs (StringBuffer. to)
               :clj (StringBuilder. (str to)))
         rf (fn [s x]
              (cond
                (nil? x) x
                (string? s)
                (if (str/blank? x)
                  s
                  #?(:cljs (.append s x)
                     :clj (.append ^StringBuilder s ^String x)))
                :else
                (let [sx (str x)]
                  #?(:cljs (.append s sx)
                     :clj (.append ^StringBuilder s ^String sx)))))
         sb (reduce rf sb from)]
     #?(:cljs (.toString sb)
        :clj (str sb))))
  ([to xform from]
   (let [sb #?(:cljs (StringBuffer. to)
               :clj (StringBuilder. (str to)))
         rf (fn [s x]
              (cond
                (nil? x) x
                (string? s)
                (if (str/blank? x)
                  s
                  #?(:cljs (.append s x)
                     :clj (.append ^StringBuilder s ^String x)))
                :else
                (let [sx (str x)]
                  #?(:cljs (.append s sx)
                     :clj (.append ^StringBuilder s ^String sx)))))
         sb (transduce xform (completing rf) sb from)]
     #?(:cljs (.toString sb)
        :clj (str sb)))))

(defn preserving-reduced*
  "Returns a reducing function which preserves reduced object
  outside reduce context."
  [rf]
  #(let [ret (rf %1 %2)]
     (if (reduced? ret) (reduced ret) ret)))


;;; Explanations

(def ^:dynamic *explanation*
  "A var that controls whether explanations are enabled or not."
  false)

(def explanations
  "An atom that holds the explanations."
  (atom []))

(defn explain*
  "Adds new explanation to the current stack. Returns nil.
  Prefer explain macro."
  [& explanation-items]
  (swap! explanations conj (vec explanation-items))
  nil)

(defmacro explain
  "Adds new explanation to the current stack, if the explanations
  are enabled. Returns nil.
  title - a string name.
  short - values that will be printed on same line as title
  items - each item will be pretty printed on a separate line"
  [title short & items]
  `(when *explanation*
     (explain* ~title ~short ~@items)))

#?(:clj
   (defmacro with-explanations
     "Enables explanations, evals body and returns [explanations ret]
     tuple."
     [& body]
     `(with-bindings {#'*explanation* true}
        (reset! explanations [])
        (let [ret# (do ~@body)]
          [@explanations ret#]))))

#?(:clj
   (defn pprint-explanations
     "Pretty prints given or global explanations. Returns nil."
     ([]
      (pprint-explanations @explanations))
     ([explanations]
      (println " ")
      (println "=====  EXPLANATIONS  =====")
      (doseq [[title short & items] explanations]
        (let [pf #(with-out-str (pprint %))
              pv #(cond (vector? %) % (nil? %) [] :else [%])
              lines (map pf items)]
          (println "===" (str title ":") (str/join ", " (pv short)))
          (println (apply str lines))))
      nil)))

;; DEPRECATED
(defn remove-non-word-chars [s]
  (when s (str/trim
           (str/replace s
                        #?(:clj #"[^\p{L}\p{Nd}]+"
                           :cljs #"[\W_]+")
                        " "))))

(defn url? [term]
  (when term
    (boolean (re-matches #"^https?://.*" term))))

;; DEPRECATED
(defn url-or-clean-term
  "Removes non word characters from terms that are not URLs."
  [term]
  (when term
    (if (url? term) term
        (remove-non-word-chars term))))

#?(:clj
   (defmacro profile-js [k & body]
     `(do
        (defonce ~'profile-stats (atom {}))
        (let [k# ~k
              t0# (js/performance.now)
              res# (do ~@body)
              t1# (js/performance.now)
              ms# (- t1# t0#)
              all-stats# (swap! ~'profile-stats
                                update k# conj ms#)
              k-stats# (get all-stats# k#)
              avg# (/ (apply + k-stats#)
                      (count k-stats#))
              report# (str k# " " ms#
                           "ms, average: " avg# "ms")]
          (js/console.info report#)
          res#))))

(defn unkeyword
  "Returns a string representation of x, retaining namespace of
  keywords."
  [x]
  (cond (string? x) x
        (keyword? x)
        #?(:clj (if-let [n (namespace x)]
                  (str n "/" (name x))
                  (name x))
           :cljs (.-fqn x))
        :else (str x)))

(defn json-type?
  "Returns true if x is of a type supported by JSON."
  [x]
  (or (string? x) (number? x) (nil? x) (boolean? x)
      (vector? x) (map? x)))

(defn jsonize
  "Returns x converted to type supported by JSON."
  [x]
  (let [pf (fn [x]
             (cond (json-type? x) x
                   (keyword? x) (unkeyword x)
                   (coll? x) (vec x)
                   :else (str x)))]
    (when x
      (walk/postwalk pf x))))

(defn ensure-sentence
  [s]
  (when s
    (if (or (str/blank? s) (contains? #{\. \! \?} (last s)))
      s
      (str s \.))))

#?(:clj
   (defmacro wrap-error
     "Catches exceptions thrown from body and rethrows them as ex-infos."
     {:style/indent 1}
     [msg & body]
     `(try
        ~@body
        (catch Exception e#
          (let [t# (keyword (name (ns-name *ns*)) "error")]
            (throw (ex-info ~msg {:type t#} e#)))))))

#?(:clj
   (defn new-tmp-dir
     []
     (->
      (java.nio.file.Files/createTempDirectory
       ""
       (into-array java.nio.file.attribute.FileAttribute []))
      (.toFile))))

#?(:clj
   (defmacro with-tmp-dir
     "Evaluates body while binding `tmp-dir-bind` to a fresh temporary
  directory and returns evaluated value. Cleans up directory
  afterwards, if no exception happens. Else, leaves it there for
  inspection."
     [tmp-dir-bind & body]
     `(let [~tmp-dir-bind (new-tmp-dir)
            res# (do ~@body)]
        (org.apache.commons.io.FileUtils/deleteQuietly
         ~tmp-dir-bind)
        res#)))

(defn str->int
  [x]
  #?(:clj
     (try
       (Long/parseLong x)
       (catch Exception e 0))
     :cljs
     (try
       (js/parseInt x)
       (catch js/Object e 0))))

(defn mlower
  [s]
  (when-not (str/blank? s)
    (str/lower-case s)))

(defn keyword-val
  [s]
  (when-let [s (mlower s)]
    (str/replace s #"\W" "-")))

(defn str-replace
  "Repeated version of str/replace. Accepts an ordered sequence of
  replacement pairs which are passed to clojure.string/replace."
  [s replacements]
  (assert (and (sequential? replacements)
               (every? #(and (sequential? %)
                             (= 2 (count %)))
                       replacements))
          (str "Replacements: " replacements))
  (reduce (fn [s [k v]]
            (str/replace s k v))
          s replacements))

(defn resolve-url
  "Returns absolute url by resolving relative url to domain + path of
  absolute url."
  [absolute relative]
  (str
   (.resolve (#?(:cljs goog.Uri.
                 :clj java.net.URI.) (str/trim absolute))
             (#?(:cljs goog.Uri.
                 :clj java.net.URI.) (str/trim relative)))))

(defn keyword-identical?
  [v1 v2]
  #?(:clj (identical? v1 v2)
     :cljs (cljs.core/keyword-identical? v1 v2)))

(defn not-blank
  [x]
  (when-not (str/blank? x)
    x))

(defn mmax
  [coll]
  (when (seq coll)
    (apply max coll)))

(defn mmin
  [coll]
  (when (seq coll)
    (apply min coll)))

(defn renamespace-keys
  "Namespace all keys in a map whether they have namespace or not."
  [m nspc]
  (->> m
       (map (fn [[k v]] [(keyword nspc (name k)) v]))
       (into {})))

;;;; Scratch

#?(:clj
   (comment

     (keyword-identical? :foo :foo)
     (keyword-identical? :foo :bar)
     ))

#?(:clj
   (defn get-local-ip-addr
     []
     (let [socket (java.net.DatagramSocket.)
           _ (.connect socket (java.net.InetAddress/getByName "8.8.8.8") 10002)
           addr (.getHostAddress (.getLocalAddress socket))]
       (.close socket)
       addr)))

#?(:clj
   (defn service-name
     [cfg]
     (str (or (get-in cfg [:dre :target]) "unknown")
          "-"
          (or (get-in cfg [:hostname])
              (get-local-ip-addr)))))


(defn in? [coll value]
  (boolean (some #(= value %) coll)))

#?(:cljs
   (defn stop-propagation
     [ev]
     (.preventDefault ev)
     (.stopPropagation ev)))

(defn join-para
  [coll]
  (if (< 1 (count coll))
    (let [mf (fn [s] (if (str/ends-with? s ".") s (str s ".")))
          coll (remove str/blank? coll)
          coll (map str/trim coll)
          coll (remove str/blank? coll)
          last (last coll)
          coll (butlast coll)
          coll (map mf coll)]
      (str/join "\n\n" (concat coll [last])))
    (first coll)))

;; TODO: C/P'ed from dre.page-util
(defn- strip-zerowidth-utf8
  [s]
  (str/replace s #"(^[\s\u200b]*|[\s\u200b]*$)" ""))

(defn mtrim
  [s]
  (when s
    (strip-zerowidth-utf8 (str/trim s))))
